import { call, all, fork, put, takeLatest } from 'redux-saga/effects';
import { isolateMosaicLayers } from '../utils/layerData/mosaic';
import { extractSoilSsurgo } from '../utils/layerData/mosaic/soilSsurgo';
import { logDmbErrorLocation } from '../utils/error';
import {
  REQUEST_SOIL_SSURGO_ZONES,
  REQUEST_IMPORTED_SHAPEFILE_DATA,
} from '../constants/actionTypes';

import {
  receiveSoilSsurgoZones,
  soilSsurgoZonesFailure,
  receiveImportedShapefileData,
  importedShapefileDataFailure,
  getMosaicLayersFailure
} from '../actions/mosaicActions';

import {
  requestMosaicV3Layers,
  requestSoilSsurgoLatest,
  requestSoilSsurgoZones,
  requestImportedShapefileData,
} from '../requesters/mosaicRequester';

import type { SagaType } from './types';

interface MosaicGenericResponse {
  data: Record<string, unknown>[];
}

interface SsurgoResponse {
  data: Record<string, unknown>;
}

interface SoilSsurgoZonesAction {
  type: string;
  imageId: string;
}

interface SoilSsurgoZonesResponse {
  features: Record<string, unknown>[];
}

export function* getSoilSsurgo(fieldId: string, userId: number) {
  try {
    const ssurgo: SsurgoResponse = yield call(requestSoilSsurgoLatest, fieldId, userId);
    return ssurgo;
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getSoilSsurgo');
    return {};
  }
}

export function* getMosaicV3Layers(fieldId: string, userId: number) {
  try {
    const mosaicGeneric: MosaicGenericResponse = yield call(requestMosaicV3Layers, fieldId, userId);
    return mosaicGeneric;
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getMosaicV3Layers');
    return yield put(getMosaicLayersFailure());
  }
}

export function* getMosaicData(fieldId: string, userId: number) {
  const [mosaicGeneric, ssurgo]: [MosaicGenericResponse, SsurgoResponse] = yield all([
    call(getMosaicV3Layers, fieldId, userId),
    call(getSoilSsurgo, fieldId, userId)
  ]);

  const { seasonAgnostic, seasonSpecific } = isolateMosaicLayers(
    mosaicGeneric.data
  );
  const ssurgoSeasonData = extractSoilSsurgo(ssurgo.data);

  return {
    seasonSpecific,
    seasonAgnostic: { ...seasonAgnostic, ...ssurgoSeasonData },
  };
}

export function* getImportedShapefileData(action: { type: string } & Record<string, unknown>) {
  try {
    const response: Record<string, unknown> = yield call(requestImportedShapefileData, action);
    yield put(receiveImportedShapefileData(response));
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga - getImportedShapefileZones');
    yield put(importedShapefileDataFailure());
  }
}

export function* getSoilSsurgoZones(action: SoilSsurgoZonesAction) {
  try {
    const response: SoilSsurgoZonesResponse = yield call(requestSoilSsurgoZones, action.imageId);
    yield put(receiveSoilSsurgoZones(response.features));
  } catch (error) {
    logDmbErrorLocation(error, 'mosaic saga');
    yield put(soilSsurgoZonesFailure());
  }
}

export function* getImportedShapefilesDataWatcher(): SagaType {
  yield takeLatest(REQUEST_IMPORTED_SHAPEFILE_DATA, getImportedShapefileData);
}

export function* getSoilSsurgoZonesWatcher(): SagaType {
  yield takeLatest(REQUEST_SOIL_SSURGO_ZONES, getSoilSsurgoZones);
}

export default function* rootSaga(): SagaType {
  yield fork(getImportedShapefilesDataWatcher);
  yield fork(getSoilSsurgoZonesWatcher);
}
